import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { environment } from 'src/environments/environment';

// Super-admin page for parents' refund requests (customer portal, parent area).
//
// The amount is already OFF the child's card when a parent asks, and the server
// has already checked it against what the parent paid in — so this page only
// decides: Approve pays it back to the card/Vipps payments it came from;
// Reject puts it back on the card. Both are enforced server-side
// (/guardianAdmin, functions/services/guardianService.js); an approve that
// failed at Stripe/Vipps shows as "Failed" and can simply be approved again.
@Component({
  selector: 'parent-refunds',
  template: `
    <div class="pr-wrap">
      <div class="pr-empty" *ngIf="!isSuperAdmin && !loading">Only a super-admin can handle parent refunds.</div>

      <ng-container *ngIf="isSuperAdmin">
        <div class="pr-toolbar">
          <p class="pr-note">
            Refunds parents have asked for from their children's canteen cards. The money is already off the card.
            <strong>Approve</strong> pays it back to the payment it came from (card or Vipps).
            <strong>Reject</strong> puts it back on the card.
          </p>
          <span class="pr-spacer"></span>
          <mat-button-toggle-group [(ngModel)]="status" (change)="load()">
            <mat-button-toggle *ngFor="let s of statuses" [value]="s.value">{{ s.label }}</mat-button-toggle>
          </mat-button-toggle-group>
        </div>

        <div class="pr-state" *ngIf="loading">Loading…</div>
        <div class="pr-state pr-error" *ngIf="error">{{ error }}</div>
        <div class="pr-state" *ngIf="!loading && !error && !rows.length">Nothing here.</div>

        <table class="pr-table" *ngIf="!loading && rows.length">
          <thead>
            <tr>
              <th>Requested</th><th>Parent</th><th>Child</th><th class="num">Amount</th><th>Reason</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let r of rows">
              <td>{{ r.when }}</td>
              <td>{{ r.guardianName }}<div class="pr-muted">{{ r.guardianEmail }}</div></td>
              <td>{{ r.kidName }}</td>
              <td class="num">{{ r.amountText }}</td>
              <td class="pr-reason">{{ r.reason || '—' }}</td>
              <td>
                <span class="pr-chip" [ngClass]="r.chip">{{ r.statusLabel }}</span>
                <div class="pr-muted" *ngIf="r.error">{{ r.error }}</div>
                <div class="pr-muted" *ngIf="r.decidedBy">by {{ r.decidedBy }}</div>
              </td>
              <td class="pr-actions">
                <ng-container *ngIf="r.status === 'requested' || r.status === 'error'">
                  <button mat-flat-button color="primary" (click)="approve(r)" [disabled]="busy[r.id]">
                    {{ r.status === 'error' ? 'Retry payout' : 'Approve' }}
                  </button>
                  <button mat-stroked-button color="warn" (click)="reject(r)" [disabled]="busy[r.id]" *ngIf="r.status === 'requested'">Reject</button>
                </ng-container>
              </td>
            </tr>
          </tbody>
        </table>
      </ng-container>
    </div>
  `,
  styles: [`
    .pr-wrap { padding: 16px; }
    .pr-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
    .pr-note { margin: 0; color: #555; font-size: 13px; max-width: 720px; }
    .pr-spacer { flex: 1 1 auto; }
    .pr-state { padding: 28px; text-align: center; color: #888; }
    .pr-error { color: #c0392b; }
    .pr-table { width: 100%; border-collapse: collapse; background: #fff; font-size: 13px; }
    .pr-table th, .pr-table td { text-align: left; padding: 10px 12px; border-bottom: 1px solid #eef0f2; vertical-align: top; }
    .pr-table th { color: #888; font-weight: 500; font-size: 12px; text-transform: uppercase; }
    .num { text-align: right !important; font-variant-numeric: tabular-nums; }
    .pr-muted { color: #888; font-size: 12px; }
    .pr-reason { max-width: 260px; white-space: pre-wrap; }
    .pr-actions { white-space: nowrap; text-align: right; }
    .pr-actions button { margin-left: 6px; }
    .pr-chip { border-radius: 12px; padding: 3px 10px; font-size: 12px; font-weight: 600; background: #eef1f4; color: #555; white-space: nowrap; }
    .pr-chip-ok { background: #e8f5e9; color: #2e7d32; }
    .pr-chip-warn { background: #fdecea; color: #c0392b; }
    .pr-chip-wait { background: #fff4e0; color: #8a5a12; }
  `]
})
export class ParentRefundsComponent implements OnInit {

  isSuperAdmin = false;
  loading = true;
  error = '';
  status = 'requested';
  rows: any[] = [];
  busy: { [id: string]: boolean } = {};

  readonly statuses = [
    { value: 'requested', label: 'Waiting' },
    { value: 'error', label: 'Failed payouts' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'withdrawn', label: 'Withdrawn' }
  ];
  private readonly labels: any = {
    requested: 'Waiting', processing: 'Paying out', error: 'Payout failed', refunded: 'Refunded', rejected: 'Rejected', withdrawn: 'Withdrawn by parent'
  };

  private base = environment.apiUrl + '/widgets/guardianAdmin';

  constructor(private http: HttpClient, private auth: AuthService, private notifier: NotificationService) { }

  async ngOnInit() {
    const user: any = await this.auth.getUserPromise();
    this.isSuperAdmin = this.auth.isSuperAdmin(user);
    if (this.isSuperAdmin) { await this.load(); } else { this.loading = false; }
  }

  private async headers() {
    const idToken = await this.auth.getToken();
    return { headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }) };
  }

  async load() {
    this.loading = true; this.error = '';
    try {
      const res: any = await this.http.get(`${this.base}/refunds?status=${this.status}`, await this.headers()).toPromise();
      // Formatted once into fields, never in getters bound to *ngFor.
      this.rows = (res.refunds || []).map((r: any) => ({
        ...r,
        when: r.createdAt ? moment(r.createdAt).tz('Europe/Oslo').format('DD.MM.YY HH:mm') : '',
        amountText: Number(r.amount || 0).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr',
        statusLabel: this.labels[r.status] || r.status,
        chip: r.status === 'refunded' ? 'pr-chip-ok' : (['error', 'rejected'].includes(r.status) ? 'pr-chip-warn' : (r.status === 'requested' ? 'pr-chip-wait' : ''))
      }));
    } catch (err: any) {
      this.error = (err && err.error && err.error.message) || 'Could not load refund requests';
    }
    this.loading = false;
  }

  async approve(r: any) {
    if (!confirm(`Pay ${r.amountText} back to ${r.guardianName || r.guardianEmail} (${r.kidName}'s card)?`)) { return; }
    await this.act(r, 'approve', 'Refund paid out');
  }

  async reject(r: any) {
    if (!confirm(`Reject this refund? ${r.amountText} goes back on ${r.kidName}'s card.`)) { return; }
    await this.act(r, 'reject', 'Rejected: the amount is back on the card');
  }

  private async act(r: any, action: 'approve' | 'reject', done: string) {
    this.busy[r.id] = true;
    try {
      await this.http.post(`${this.base}/refunds/${r.id}/${action}`, '{}', await this.headers()).toPromise();
      this.notifier.showNotification('success', done);
    } catch (err: any) {
      const body = (err && err.error) || {};
      const message = body.message === 'refund_failed'
        ? `The ${body.provider || 'payment'} refund failed (${body.detail || 'unknown error'}). Nothing was paid twice. Try again, or refund it by hand.`
        : (body.message || 'Could not update the refund');
      this.notifier.showNotification('error', message);
    }
    this.busy[r.id] = false;
    await this.load();
  }
}
